import Vue, { ref, computed } from "vue";
import { useStore } from "@/composables/store";
import * as getters from "@/store/getters";
import {
  useQuery,
  useInfiniteQuery,
  useQueryClient,
  useMutation,
} from "@tanstack/vue-query";
import securityApi from "@/api/security";
import { isNullish, isArray, clone } from "remeda";
import { EventBus } from "@/tools/event-bus";
import { toValue } from "@vueuse/core";
import { useI18n } from "./i18n";

/** @import {Ref} from 'vue' */
/** @import {MaybeRefOrGetter} from '@vueuse/core' */

const STALE_TIME = 1000 * 60 * 5;
const REFETCH_INTERVAL = STALE_TIME;

const INITIAL_PAGE = 1;

export function useInfoNotificationsCountQuery() {
  const store = useStore();

  const customerId = computed(() => {
    return store.getters[getters.CUSTOMER_ID];
  });

  return useQuery({
    queryKey: ["notifications", "info", customerId, "count"],
    queryFn: async () => {
      if (isNullish(customerId.value)) {
        return 0;
      }

      const { data } = await securityApi.countNotification("info");

      return data;
    },
    staleTime: STALE_TIME,
    refetchInterval: REFETCH_INTERVAL,
  });
}

export function useInfoNotificationsQuery() {
  const store = useStore();

  const customerId = computed(() => {
    return store.getters[getters.CUSTOMER_ID];
  });

  return useQuery({
    queryKey: ["notifications", "info", customerId],
    queryFn: async () => {
      if (isNullish(customerId.value)) {
        return [];
      }

      const { data } = await securityApi.getNotification("info");

      return data;
    },
    staleTime: STALE_TIME,
  });
}

export function useInfoNotificationsQueryPaginated() {
  const pages = ref(INITIAL_PAGE);

  const store = useStore();

  const customerId = computed(() => {
    return store.getters[getters.CUSTOMER_ID];
  });

  return useInfiniteQuery({
    queryKey: ["notifications", "info", customerId],
    queryFn: async ({ pageParam }) => {
      if (isNullish(customerId.value)) {
        return [];
      }

      if (pageParam === 0) {
        return [];
      }

      const { data } = await securityApi.getNotificationPaginated(
        "info",
        pageParam
      );

      pages.value = data.pages;

      return data._embedded.items;
    },
    initialPageParam: INITIAL_PAGE,
    getNextPageParam: (_lastPage, _allPages, lastPageParam) => {
      if (lastPageParam >= pages.value) {
        return undefined;
      }

      return lastPageParam + 1;
    },
    getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
      if (firstPageParam <= INITIAL_PAGE) {
        return undefined;
      }

      return firstPageParam - 1;
    },
    staleTime: STALE_TIME,
  });
}

export function useCritNotificationsQuery() {
  const store = useStore();

  const customerId = computed(() => {
    return store.getters[getters.CUSTOMER_ID];
  });

  const isImpersonating = computed(() => {
    return store.getters[getters.GET_IMPERSONATE];
  });

  return useQuery({
    queryKey: ["notifications", "crit", customerId],
    queryFn: async () => {
      if (isNullish(customerId.value)) {
        return 0;
      }

      const { data } = await securityApi.getNotification("crit");

      if (data.length > 0 && !isImpersonating.value) {
        for (const notification of data) {
          EventBus.$emit("fcm", notification);
        }
      }

      return data;
    },
    staleTime: STALE_TIME,
  });
}

/**
 * @param {MaybeRefOrGetter<number>} notificationId
 */
export function useInfoNotificationDeleteMutation(notificationId) {
  const i18n = useI18n();
  const store = useStore();

  const customerId = computed(() => {
    return store.getters[getters.CUSTOMER_ID];
  });

  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["notifications", "info", customerId, "mutate"],
    mutationFn: async () => {
      return securityApi.readNotification(toValue(notificationId));
    },
    onMutate: async () => {
      const notificationIdValue = toValue(notificationId);

      await queryClient.cancelQueries({
        queryKey: ["notifications", customerId],
      });

      const previousNotifications = queryClient.getQueryData([
        "notifications",
        "info",
        customerId,
      ]);

      queryClient.setQueryData(
        ["notifications", "info", customerId],
        (/** @type {any} */ oldData) => {
          if (!isArray(oldData)) {
            return oldData;
          }

          /** @type {any} */
          const clonedData = clone(oldData);

          const notificationIndex = clonedData.findIndex((notification) => {
            return notification.id === notificationIdValue;
          });

          if (notificationIndex !== -1) {
            clonedData.splice(notificationIndex, 1);
          }

          return clonedData;
        }
      );

      return { previousNotifications };
    },
    onSuccess: () => {
      /** @type {number} */
      const count = queryClient.getQueryData([
        "notifications",
        "info",
        customerId,
        "count",
      ]);

      if (isNullish(count)) {
        queryClient.setQueryData(
          ["notifications", "info", customerId, "count"],
          0
        );
      } else {
        queryClient.setQueryData(
          ["notifications", "info", customerId, "count"],
          count - 1
        );
      }
    },
    onError: (_error, _variables, context) => {
      // @ts-ignore
      Vue.noty.error(i18n.t("messages.error.generic"));

      queryClient.setQueryData(
        ["notifications", "info", customerId],
        context.previousNotifications
      );
    },
  });
}

/**
 * @param {Ref<Array<number>|Set<number>>} notificationIds
 */
export function useInfoNotificationsDeleteMutation(notificationIds) {
  const i18n = useI18n();
  const store = useStore();

  const customerId = computed(() => {
    return store.getters[getters.CUSTOMER_ID];
  });

  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["notifications", "info", customerId, "mutate"],
    mutationFn: async () => {
      return securityApi.readNotifications(
        Array.from(toValue(notificationIds))
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["notifications", "info", customerId],
      });
    },
    onSuccess: () => {
      if (notificationIds.value instanceof Set) {
        notificationIds.value.clear();
      }

      if (isArray(notificationIds.value)) {
        notificationIds.value = [];
      }
    },
    onError: () => {
      // @ts-ignore
      Vue.noty.error(i18n.t("messages.error.generic"));
    },
  });
}
